// ZoomControl.jsx
// Zoom control for the editor canvas — Fit Width / Fit Page / preset
// percentages, plus +/- steppers. Mirrors the Docs/Word zoom bar convention:
// fit-width is the default so pages never require horizontal scrolling out of
// the box, and explicit zoom is one click away for detail work.
//
// Two shells, one control:
//   variant="inline"   — a plain group, sized to sit inside the editor status
//                        bar (its current home).
//   variant="floating" — the original self-contained pill that floated over
//                        the canvas. Kept so the control can still be dropped
//                        anywhere that has no bar to live in.
import React, { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';

const PRESETS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const STEP = 0.1;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 2;

const FLOATING_SHELL = {
  position: 'absolute', bottom: 72, right: 16, zIndex: 20,
  bgcolor: '#5A52A3', border: '1px solid #D4AF37',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
};

export default function ZoomControl({ zoomMode, zoom, onSetMode, onSetZoom, variant = 'floating' }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const label = zoomMode === 'fit-width' ? 'Fit Width'
    : zoomMode === 'fit-page'  ? 'Fit Page'
    : `${Math.round(zoom * 100)}%`;

  const step = (dir) => {
    const next = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom + dir * STEP));
    onSetZoom(next);
  };

  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5, py: 0.5,
        ...(variant === 'inline' ? {} : FLOATING_SHELL),
      }}
    >
      <IconButton size="small" onClick={() => step(-1)} title="Zoom out" sx={{ color: '#D4AF37' }}>
        <RemoveIcon sx={{ fontSize: 16 }} />
      </IconButton>

      <Typography
        component="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          all: 'unset', cursor: 'pointer', color: '#F5D76E', fontSize: '12px',
          fontWeight: 600, minWidth: 72, textAlign: 'center', px: 0.5,
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {label}
      </Typography>

      <IconButton size="small" onClick={() => step(1)} title="Zoom in" sx={{ color: '#D4AF37' }}>
        <AddIcon sx={{ fontSize: 16 }} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MenuItem onClick={() => { onSetMode('fit-width'); setAnchorEl(null); }} selected={zoomMode === 'fit-width'}>
          Fit Width
        </MenuItem>
        <MenuItem onClick={() => { onSetMode('fit-page'); setAnchorEl(null); }} selected={zoomMode === 'fit-page'}>
          Fit Page
        </MenuItem>
        {PRESETS.map((p) => (
          <MenuItem
            key={p}
            onClick={() => { onSetZoom(p); setAnchorEl(null); }}
            selected={zoomMode !== 'fit-width' && zoomMode !== 'fit-page' && zoom === p}
          >
            {Math.round(p * 100)}%
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
